import React from 'react';
import { css } from 'emotion';
import PropTypes from 'prop-types';
import {
  cssColorValue,
  Dropdown,
  DropdownList,
  DropdownLink,
  DropdownTrigger,
  LoadingIndicator,
} from '@spotify-internal/encore-web';
import { useA11yDropdown } from 'hooks/useA11yDropdown';
import Overlay from 'shared/Overlay';
import Image from 'shared/Image';
import ColorPicker from 'shared/ColorPicker';
import Icon from 'shared/Icon';
import Font from 'shared/Font';
import { FieldToggle } from 'components/encore/form/Field';
import { Modal } from '@spotify-internal/anchor-ds';
import TextImage from './components/TextImage';
import { FONTS } from '../../constants';
import styles from './EditTextScene.sass';
import {
  ToggleContainer,
  VerticalAlignmentLabel,
  HorizontalJustificationLabel,
  ColorLabel,
  ColorPickerButton,
  HiddenInput,
  InputContainer,
  VerticalAlignmentContainer,
  ContentOverlay,
  LoadingImage,
  ModalBody,
  ColorContainer,
  FieldLabelContainer,
} from './styles';

const noop = () => null;

export const EditTextScene = ({
  croppedCoverArtImageUrl,
  onToggleApplyText,
  textImageUrl,
  maybeSelectedFontId,
  textHex,
  isTextShowing,
  onSelectFont,
  onSelectJustification,
  justification,
  onSelectAlignment,
  alignment,
  onSelectColor,
  isTextColorLoading,
  isTextImageLoading,
  isColorPickerShowing,
  isAnchorWatermarkShowing,
  onClickColorPickerHide,
  onClickColorPickerShow,
  setHasAnchorBranding,
}) => {
  const dropdownListId = 'font-dropdown-list';
  const triggerId = 'font-dropdown-toggle';

  const {
    show,
    setShow,
    selected,
    setSelected,
    onClick,
    onHide,
    onKeyDown,
  } = useA11yDropdown({
    dropdownListId,
    triggerId,
    itemsLength: FONTS.length,
    initialSelected: maybeSelectedFontId || 0,
  });

  return (
    <>
      <Modal.Title>Add text</Modal.Title>
      <ModalBody>
        <div>
          <div
            className={css`
              position: relative;
              display: inline-block;
              border-radius: 8px;
              overflow: hidden;
            `}
          >
            <Image
              renderLoadingPlaceholder={() => (
                <div
                  className={css`
                    width: 200px;
                    height: 200px;
                    background-color: ${cssColorValue('backgroundTintedBase')};
                  `}
                />
              )}
              height={200}
              width={200}
              imageUrl={croppedCoverArtImageUrl}
            />
            {isTextShowing && (
              <div
                className={css`
                  position: absolute;
                  top: 0;
                  left: 0;
                `}
              >
                {isTextImageLoading || isTextColorLoading ? (
                  <LoadingImage>
                    <LoadingIndicator indicatorSize={LoadingIndicator.sm} />
                  </LoadingImage>
                ) : (
                  <TextImage
                    imageUrl={textImageUrl}
                    hex={textHex}
                    width={200}
                    height={200}
                  />
                )}
              </div>
            )}
            {isAnchorWatermarkShowing && (
              <div
                className={css`
                  position: absolute;
                  top: 0;
                  right: 0;
                  width: 28px;
                  margin: 16px 16px 0 0;
                `}
              >
                <Icon type="anchor_logo" fillColor="white" />
              </div>
            )}
          </div>

          <ToggleContainer>
            <FieldLabelContainer htmlFor="apply-toggle">
              Apply Text
            </FieldLabelContainer>
            <FieldToggle
              id="apply-toggle"
              value={isTextShowing}
              onClick={onToggleApplyText}
            />
          </ToggleContainer>
          <ToggleContainer>
            <FieldLabelContainer htmlFor="show-anchor-cover-art-logo">
              Show Anchor logo on cover art
            </FieldLabelContainer>
            <FieldToggle
              id="show-anchor-cover-art-logo"
              value={isAnchorWatermarkShowing}
              onClick={() => setHasAnchorBranding(!isAnchorWatermarkShowing)}
            />
          </ToggleContainer>
        </div>
        <div
          className={css`
            position: relative;
            flex: 0;
          `}
        >
          <div
            className={css`
              display: grid;
              grid-gap: 16px;
              text-align: left;
            `}
          >
            <div>
              <FieldLabelContainer htmlFor={triggerId}>
                Font
              </FieldLabelContainer>
              <DropdownTrigger
                overlay={
                  show && (
                    <DropdownList
                      onKeyDown={e => onKeyDown(e)}
                      onBlur={() => setShow(false)}
                      id={dropdownListId}
                      aria-activedescendant={selected}
                    >
                      {FONTS.map(({ id, name }, index) => (
                        <DropdownLink
                          as="li"
                          role="option"
                          id={id}
                          key={id}
                          onFocus={() => setSelected(index)}
                          onClick={e => {
                            onClick(e, index);
                            onSelectFont(FONTS[index]);
                          }}
                          selected={selected === id}
                          aria-selected={selected === id}
                        >
                          <Font fontName={id}>
                            <div style={{ fontSize: 20 }}>{name}</div>
                          </Font>
                        </DropdownLink>
                      ))}
                    </DropdownList>
                  )
                }
                onShow={() => setShow(true)}
                onHide={onHide}
              >
                <Dropdown id={triggerId} aria-expanded={show || undefined}>
                  {FONTS[selected] && (
                    <Font fontName={FONTS[selected].id}>
                      {FONTS[selected].name}
                    </Font>
                  )}
                </Dropdown>
              </DropdownTrigger>
            </div>
            <div>
              <FieldLabelContainer>Horizontal Alignment</FieldLabelContainer>
              <div
                className={css`
                  display: flex;
                  justify-content: space-between;
                  margin-top: 10px;
                `}
              >
                {[
                  { justificationValue: 'left', label: 'Text left-aligned' },
                  {
                    justificationValue: 'center',
                    label: 'Text center-aligned',
                  },
                  {
                    justificationValue: 'right',
                    label: 'Text right-aligned',
                  },
                ].map(({ justificationValue, label }) => (
                  <InputContainer key={justificationValue}>
                    <HiddenInput
                      id={`justification-${justificationValue}`}
                      type="radio"
                      name="justification"
                      onClick={() => onSelectJustification(justificationValue)}
                      checked={justification === justificationValue}
                      aria-label={label}
                    />
                    <HorizontalJustificationLabel
                      htmlFor={`justification-${justificationValue}`}
                    >
                      <Icon type={`justified_${justificationValue}`} />
                    </HorizontalJustificationLabel>
                  </InputContainer>
                ))}
              </div>
            </div>
            <div>
              <FieldLabelContainer>Vertical Alignment</FieldLabelContainer>
              <div
                className={css`
                  display: flex;
                  justify-content: space-between;
                  margin: 4px 0;
                `}
              >
                {[
                  {
                    text: 'Top',
                    alignmentValue: 'top',
                  },
                  {
                    text: 'Center',
                    alignmentValue: 'center',
                  },
                  {
                    text: 'Bottom',
                    alignmentValue: 'bottom',
                  },
                ].map(({ text, alignmentValue }) => (
                  <VerticalAlignmentContainer key={alignmentValue}>
                    <HiddenInput
                      id={`alignment-${alignmentValue}`}
                      type="radio"
                      name="alignment"
                      checked={alignment === alignmentValue}
                      onClick={() => onSelectAlignment(alignmentValue)}
                    />
                    <VerticalAlignmentLabel for={`alignment-${alignmentValue}`}>
                      {text}
                    </VerticalAlignmentLabel>
                  </VerticalAlignmentContainer>
                ))}
              </div>
            </div>
            <div>
              <FieldLabelContainer>Color</FieldLabelContainer>

              <ColorContainer>
                {[
                  { color: '#292f36', label: 'Black text color' },
                  { color: '#ffffff', label: 'White text color' },
                  { color: '#dfe0e1', label: 'Light grey text color' },
                  { color: '#979797', label: 'Grey text color' },
                  { color: '#5000b9', label: 'Purple text color' },
                  { color: '#2dcfb3', label: 'Teal text color' },
                ].map(({ color, label }) => (
                  <InputContainer key={color}>
                    <HiddenInput
                      type="radio"
                      name="color"
                      id={`color-${color.substring(1)}`}
                      onClick={() => onSelectColor(color)}
                      checked={textHex === color}
                      aria-label={label}
                    />
                    <ColorLabel
                      color={color}
                      for={`color-${color.substring(1)}`}
                    >
                      {textHex === color && (
                        <div
                          className={css`
                            width: 20px;
                          `}
                        >
                          <Icon type="checkmark" />
                        </div>
                      )}
                    </ColorLabel>
                  </InputContainer>
                ))}
                <ColorPickerButton
                  onClick={e => {
                    e.stopPropagation();
                    onClickColorPickerShow();
                  }}
                  aria-label="Open color picker"
                >
                  <Icon type="eye_dropper" fillColor="#979797" />
                </ColorPickerButton>
                {/* Positioned relative to surrounding box */}
                <Overlay
                  isShowing={isColorPickerShowing}
                  onClickOutside={onClickColorPickerHide}
                  rootClose
                  container={this}
                  renderContent={() => (
                    <div className={styles.colorPickerOverlay}>
                      <ColorPicker
                        hex={textHex}
                        onSelectColor={onSelectColor}
                        disableAlpha
                      />
                    </div>
                  )}
                />
              </ColorContainer>
              {!isTextShowing && <ContentOverlay />}
            </div>
          </div>
        </div>
      </ModalBody>
    </>
  );
};

EditTextScene.defaultProps = {
  croppedCoverArtImageUrl: '',
  onToggleApplyText: noop,
  textImageUrl: '',
  textHex: '#ffffff',
  isTextShowing: true,
  onSelectFont: noop,
  onSelectJustification: noop,
  justification: 'center',
  onSelectAlignment: noop,
  alignment: 'top',
  onSelectColor: noop,
  isTextImageLoading: false,
  maybeSelectedFontId: null,
  isColorPickerShowing: false,
  isAnchorWatermarkShowing: false,
  onClickColorPickerHide: noop,
  onClickColorPickerShow: noop,
  isTextColorLoading: false,
};

EditTextScene.propTypes = {
  croppedCoverArtImageUrl: PropTypes.string,
  textImageUrl: PropTypes.string,
  textHex: PropTypes.string,
  isTextShowing: PropTypes.bool,
  onSelectFont: PropTypes.func,
  onToggleApplyText: PropTypes.func,
  onSelectJustification: PropTypes.func,
  justification: PropTypes.oneOf(['left', 'center', 'right']),
  onSelectAlignment: PropTypes.func,
  alignment: PropTypes.oneOf(['top', 'center', 'bottom']),
  onSelectColor: PropTypes.func,
  isTextImageLoading: PropTypes.bool,
  maybeSelectedFontId: PropTypes.string,
  isColorPickerShowing: PropTypes.bool,
  isAnchorWatermarkShowing: PropTypes.bool,
  onClickColorPickerHide: PropTypes.func,
  onClickColorPickerShow: PropTypes.func,
  isTextColorLoading: PropTypes.bool,
};
